import React, { useEffect, useRef, useState } from 'react';
import { FaBell, FaUserCircle, FaChevronDown, FaSignOutAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FaPlus } from "react-icons/fa6";
import { FiEdit3 } from 'react-icons/fi';
import { MdOutlineArrowOutward } from 'react-icons/md';
import { IoPerson } from "react-icons/io5";
import { MdOutlineMailOutline } from "react-icons/md";
import axios from 'axios';
import { toast } from 'react-toastify';

const SuperAdminFrontOffice = () => {
  // Dropdown state & refs
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  // Data and loading states
  const [frontOfficeUsers, setFrontOfficeUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which user's status is being updated

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch front office users
  const fetchFrontOfficeUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://bmrbackend.dynastymaker.com/api/users/front-office', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success && Array.isArray(response.data.data)) {
        setFrontOfficeUsers(response.data.data);
        setTotalPages(Math.ceil(response.data.data.length / itemsPerPage));
      } else {
        setFrontOfficeUsers([]);
        toast.error('No front office users found');
      }
    } catch (error) {
      console.error('Error fetching front office users:', error);
      toast.error('Failed to fetch front office users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFrontOfficeUsers();
  }, []);

  // Toggle user status with loading state
  const toggleUserStatus = async (id) => {
    setUpdatingStatus(id);
    
    try {
      const user = frontOfficeUsers.find(user => user.id === id);
      const newStatus = !user.status;

      await axios.put(
        `https://bmrbackend.dynastymaker.com/api/users/front-office/${id}/status`,
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      setFrontOfficeUsers(prev => prev.map(user => 
        user.id === id ? { ...user, status: newStatus } : user
      ));

      toast.success(`Status updated successfully`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Status badge helper
  const getStatusClass = (status) => {
    return status ? 'status-badge active' : 'status-badge de-active';
  };

  const getStatusText = (status) => {
    return status ? 'Active' : 'Inactive';
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = frontOfficeUsers.slice(indexOfFirstItem, indexOfLastItem);



  return (
    <div className="container dashboard">
      {/* Top bar */}
      <div className="container">
        <div className="row">
          <div className="d-flex flex-row align-items-center justify-content-end">
            {/* Notification Dropdown */}
            <div className="position-relative me-3" ref={notificationRef}>
              <button 
                className="btn btn-link text-decoration-none p-2 bg-white rounded-3 shadow-sm d-flex align-items-center"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <FaBell className="fs-4 text-muted" />
                <span className="position-absolute text-danger top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  .
                </span>
              </button>
              
              {/* Notification Dropdown Menu */}
              {showNotifications && (
                <div className="dropdown-menu show notificationadmin shadow-lg p-0">
                  <div className="p-3 border-bottom">
                    <h6 className="fw-bold mb-0">Notifications</h6>
                  </div>
                  <div className="overflow-auto" style={{ maxHeight: '300px' }}>
                    <a href="#" className="dropdown-item d-flex py-3 border-bottom">
                      <div className='text-start'>
                        <p className="mb-0 fw-medium">New message received</p>
                        <small className="text-muted">You have 2 unread messages</small>
                        <div className="text-muted small mt-1">2 hours ago</div>
                      </div>
                    </a>
                    {/* More notification items... */}
                  </div>
                  <div className="text-center py-2 border-top">
                    <a href="#" className="text-primary text-decoration-none small">View all notifications</a>
                  </div>
                </div>
              )}
            </div>
            
            {/* User Profile Dropdown */}
            <div className="position-relative" ref={profileRef}>
              <button 
                className="btn btn-link text-decoration-none p-2 bg-white rounded-3 shadow-sm d-flex align-items-center"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <div className="d-flex flex-column align-items-end me-2">
                  <span className="fw-bold text-dark">derek aivarado</span>
                  <span className="small text-muted">Admin</span>
                </div>
                <div className="position-relative">
                  <div className="bg-light border rounded-circle p-1">
                    <FaUserCircle className="fs-1 text-primary" />
                  </div>
                  <FaChevronDown className="text-muted small position-absolute bottom-0 end-0 bg-white rounded-circle p-1 border" style={{ fontSize: '0.6rem' }} />
                </div>
              </button>
              
              {/* Profile Dropdown Menu */}
              {showProfileMenu && (
                <div className="dropdown-menu show shadow-lg" style={{ width: '220px', right: 0, left: 'auto', display: 'block' }}>
                  <div className="text-center p-3 border-bottom">
                    <FaUserCircle className="fs-1 text-primary mb-2" />
                    <h6 className="mb-0 fw-bold">derek aivarado</h6>
                    <small className="text-muted">RY Admin</small>
                  </div>
                  <a href="#" className="dropdown-item d-flex align-items-center py-2">
                    <FaUserCircle className="me-3 text-muted" />
                    <span>My Profile</span>
                  </a>
                  <div className="dropdown-divider my-1"></div>
                  <a href="#" className="dropdown-item d-flex align-items-center py-2 text-danger">
                    <FaSignOutAlt className="me-3" />
                    <span>Logout</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Header & Breadcrumb */}
      <h2 className="fw-bold mb-1">Front Office</h2>
      <div className="small mb-4">
        <Link to="/super-admin/dashboard" className="text-decoration-none text-dark">DASHBOARD</Link> / <span className="fw-bold">FRONT OFFICE</span>
      </div>

      {/* Filters */}
      <div className="row align-items-center mb-4">
        <div className="col-md-3 col-6">
          <Link to="/super-admin/front-office/add" className="btn btn-outline-primary buttonadd">
            <FaPlus /> ADD FRONT OFFICE
          </Link>
        </div>
        <div className="col-md-2 col-6"><input type="text" placeholder="Keyword" className="form-control" /></div>
        <div className="col-md-2 col-6"><input type="email" placeholder="E-mail" className="form-control" /></div>
        <div className="col-md-2 col-6">
          <select className="form-control" defaultValue="">
            <option value="" disabled>Status</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
        </div>
        <div className="col-md-3 col-12"><input type="text" placeholder="Search" className="form-control" /></div>
      </div>


 {loading ? (
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
 
      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile No</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {currentItems.map((user, index) => (
              <tr key={user.id}>
                <td>{indexOfFirstItem + index + 1}</td>
                <td>
                  <IoPerson size={20} color='#af52de' /> {user.name}                   
                </td>
                <td><MdOutlineMailOutline size={20} className='text-warning' /> {user.email}</td>
                <td>{user.mobile_no || 'N/A'}</td>
                <td>
                  <div className="d-flex align-items-center">
                    <div 
                      className={`toggle-switch ${user.status ? 'active' : ''} ${updatingStatus === user.id ? 'disabled' : ''}`}
                      onClick={() => updatingStatus !== user.id && toggleUserStatus(user.id)}
                    >
                      {updatingStatus === user.id ? (
                        <div className="spinner-border text-white mx-1 spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <>
                          <div className="slider"></div>
                          <span className="active-text">ON</span>
                          <span className="inactive-text">OFF</span>
                        </>
                      )}
                    </div>
                    <span className={`ms-2 ${getStatusClass(user.status)}`}>
                      {getStatusText(user.status)}
                    </span>
                  </div>
                </td>
                <td className="d-flex gap-2">
                  <Link to={`/super-admin/show-front-office/${user.id}`} className="btn btn-sm btn-light"><MdOutlineArrowOutward /></Link>
                  <Link to={`/super-admin/edit-front-office/${user.id}`} className="btn btn-sm btn-light"><FiEdit3 /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination mt-3">
            {Array.from({ length: totalPages }, (_, idx) => (
              <button
                key={idx + 1}
                className={`page-button ${currentPage === idx + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        )}
        
      </div>
        )}
    </div>
  );
};

export default SuperAdminFrontOffice;